import React, { useEffect, useState } from 'react';
import { aboutUsApi, getMediaUrl } from '../services/api';
import '../styles/AboutPage.css';

function AboutPage() {
  const [aboutData, setAboutData] = useState(null);

  // Fetch dynamic content on mount
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await aboutUsApi.getAboutUs();
        if (res?.success && res.data) {
          setAboutData(res.data);
        }
      } catch (err) {
        console.warn('Could not load dynamic about us data, falling back to defaults:', err);
      }
    };
    fetchContent();

    setTimeout(() => {
      if (window.WOW) {
        try {
          new window.WOW().init();
        } catch (e) {
          console.log('WOW.js not available');
        }
      }
    }, 500);
  }, []);

  // Dynamic values with reliable fallbacks
  const headerTitle = aboutData?.headerTitle || 'About Us';
  const headerSubtitle = aboutData?.headerSubtitle || 'About';
  const headerBgImage = aboutData?.headerBgImage
    ? getMediaUrl(aboutData.headerBgImage)
    : '/assets/images/backgrounds/slider-2-2.jpg';
  const tagline = aboutData?.tagline || 'About PsychMitra';
  const title = aboutData?.title || 'Our Mission & Vision';
  const introText =
    aboutData?.introText ||
    'PsychMitra is dedicated to providing accessible, high-quality mental health services to everyone who needs them.';
  const missionTitle = aboutData?.missionTitle || 'Our Mission';
  const missionDescription =
    aboutData?.missionDescription ||
    'To create an innovative ecosystem connecting mental health professionals with clients, ensuring accessible, personalized, and sustainable care.';
  const visionTitle = aboutData?.visionTitle || 'Our Vision';
  const visionDescription =
    aboutData?.visionDescription ||
    'A world where mental health support is accessible, affordable, and empowering for everyone, and where professionals practice with dignity and autonomy.';
  const highlightQuote =
    aboutData?.highlightQuote ||
    'Our goal is simple yet powerful: to increase accessibility for those seeking care, and to create a fair, supportive space for those offering it.';
  const ctaText = aboutData?.ctaText || 'Get In Touch';
  const ctaLink = aboutData?.ctaLink || '#contact';

  const showTeamSection = Boolean(aboutData?.showTeamSection);
  const teamTagline = aboutData?.teamTagline || 'Our Team';
  const teamTitle = aboutData?.teamTitle || 'Meet Our Experts';
  const teamMembers = aboutData?.teamMembers || [];

  // Sequential images for each interleaved section
  const galleryImages = aboutData?.galleryImages || [];
  const imgIntro = galleryImages[0]
    ? getMediaUrl(galleryImages[0])
    : '/assets/images/new_img/Firefly_Gemini Flash_-A minimalist animation concept showing a penguin sliding down a snowy _mountain with 359813.png';
  const imgMission = galleryImages[1]
    ? getMediaUrl(galleryImages[1])
    : '/assets/images/backgrounds/slider-2-1.jpg';
  const imgVision = galleryImages[2]
    ? getMediaUrl(galleryImages[2])
    : '/assets/images/backgrounds/slider-2-2.jpg';

  return (
    <div className="pm-about-page">
      {/* Ambient Decorative Blurs */}
      <div className="pm-abt-ambient-blur blur-1"></div>
      <div className="pm-abt-ambient-blur blur-2"></div>

      {/* Hero Header */}
      <section className="pm-abt-hero">
        <div className="container text-center">
          <span className="pm-abt-pill">✨ {headerSubtitle || 'About PsychMitra'}</span>
          <h1 className="pm-abt-heading">
            {headerTitle ? (
              headerTitle === 'About Us' ? (
                <>
                  About <span className="text-gradient">PsychMitra</span>
                </>
              ) : (
                headerTitle
              )
            ) : (
              <>
                About <span className="text-gradient">PsychMitra</span>
              </>
            )}
          </h1>
          <p className="pm-abt-lead">
            {tagline || 'Empowering minds, supporting journeys, and fostering compassionate, ethical mental healthcare.'}
          </p>
        </div>
      </section>

      {/* 2. Alternating Interleaved Content & Images Flow */}
      <section className="pm-abt-story-flow">
        <div className="container">

          {/* =========================================================
              BLOCK 1: Introduction Story (Content Left -> Image Right)
              ========================================================= */}
          <div className="pm-abt-zigzag-row wow fadeInUp" data-wow-delay="100ms">
            <div className="pm-abt-zigzag-card">
              <div className="row align-items-center g-4 g-lg-5">
                <div className="col-lg-6">
                  <div className="pm-abt-text-col">
                    <div className="pm-abt-tag-pill">
                      <span className="pm-abt-pulse-dot"></span>
                      <span>{tagline}</span>
                    </div>
                    <h2 className="pm-abt-block-title">{title}</h2>
                    <p className="pm-abt-block-desc">{introText}</p>
                    <ul className="pm-abt-checklist pm-abt-checklist-purple">
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span>Ethical, compassionate, and stigma-free mental health support.</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span>Connecting individuals with verified, empathetic practitioners.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="pm-abt-visual-col">
                    <div className="pm-abt-img-frame">
                      <img
                        src={imgIntro}
                        alt="About PsychMitra Sanctuary"
                        onError={(e) => {
                          e.target.src = '/assets/images/backgrounds/slider-2-2.jpg';
                        }}
                      />
                      <div className="pm-abt-floating-badge-top">
                        <i className="fas fa-heart"></i>
                        <span>Safe & Ethical Space</span>
                      </div>
                      <div className="pm-abt-floating-badge-bottom">
                        <div className="pm-abt-badge-info">
                          <h5>PsychMitra Ecosystem</h5>
                          <p>Human First Care</p>
                        </div>
                        <div className="pm-abt-badge-icon">
                          <i className="fas fa-sparkles"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* =========================================================
              BLOCK 2: Mission Section (Image Left -> Content Right)
              ========================================================= */}
          <div className="pm-abt-zigzag-row wow fadeInUp" data-wow-delay="150ms">
            <div className="pm-abt-zigzag-card">
              <div className="row align-items-center g-4 g-lg-5 flex-lg-row-reverse">
                <div className="col-lg-6">
                  <div className="pm-abt-text-col">
                    <div className="pm-abt-tag-pill">
                      <span className="pm-abt-pulse-dot"></span>
                      <span>PURPOSE & COMMITMENT</span>
                    </div>
                    <h2 className="pm-abt-block-title">{missionTitle}</h2>
                    <p className="pm-abt-block-desc">{missionDescription}</p>
                    <ul className="pm-abt-checklist pm-abt-checklist-purple">
                      <li>
                        <i className="fas fa-bullseye"></i>
                        <span>Eliminating barriers between practitioners and clients.</span>
                      </li>
                      <li>
                        <i className="fas fa-bullseye"></i>
                        <span>Ensuring personalized, accessible, and sustainable treatment.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="pm-abt-visual-col pm-abt-visual-left">
                    <div className="pm-abt-img-frame">
                      <img
                        src={imgMission}
                        alt="Our Mission"
                        onError={(e) => {
                          e.target.src = '/assets/images/backgrounds/slider-2-1.jpg';
                        }}
                      />
                      <div className="pm-abt-floating-badge-top">
                        <i className="fas fa-bullseye"></i>
                        <span>Our Mission</span>
                      </div>
                      <div className="pm-abt-floating-badge-bottom">
                        <div className="pm-abt-badge-info">
                          <h5>Tailored Care Pathways</h5>
                          <p>Accessible to Everyone</p>
                        </div>
                        <div className="pm-abt-badge-icon">
                          <i className="fas fa-hands-helping"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* =========================================================
              BLOCK 3: Vision Section (Content Left -> Image Right)
              ========================================================= */}
          <div className="pm-abt-zigzag-row wow fadeInUp" data-wow-delay="200ms">
            <div className="pm-abt-zigzag-card">
              <div className="row align-items-center g-4 g-lg-5">
                <div className="col-lg-6">
                  <div className="pm-abt-text-col">
                    <div className="pm-abt-tag-pill pm-abt-tag-pill-emerald">
                      <span className="pm-abt-pulse-dot pm-abt-pulse-dot-emerald"></span>
                      <span>FORWARD VISION</span>
                    </div>
                    <h2 className="pm-abt-block-title">{visionTitle}</h2>
                    <p className="pm-abt-block-desc">{visionDescription}</p>
                    <ul className="pm-abt-checklist pm-abt-checklist-emerald">
                      <li>
                        <i className="fas fa-eye"></i>
                        <span>Dignity, autonomy, and fair ecosystem for practitioners.</span>
                      </li>
                      <li>
                        <i className="fas fa-eye"></i>
                        <span>Empowering every seeker with affordable, transformative wellness.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="pm-abt-visual-col">
                    <div className="pm-abt-img-frame">
                      <img
                        src={imgVision}
                        alt="Our Vision"
                        onError={(e) => {
                          e.target.src = '/assets/images/backgrounds/slider-2-3.jpg';
                        }}
                      />
                      <div className="pm-abt-floating-badge-top">
                        <i className="fas fa-eye"></i>
                        <span>Our Vision</span>
                      </div>
                      <div className="pm-abt-floating-badge-bottom">
                        <div className="pm-abt-badge-info">
                          <h5>Future of Mental Health</h5>
                          <p>Empowerment & Inclusion</p>
                        </div>
                        <div className="pm-abt-badge-icon">
                          <i className="fas fa-seedling"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* =========================================================
              BLOCK 4: Highlight Quote & Call to Action (CTA)
              ========================================================= */}
          <div className="pm-abt-quote-cta-card wow fadeInUp" data-wow-delay="250ms">
            {highlightQuote && (
              <div className="pm-abt-quote-box">
                <i className="fas fa-quote-left"></i>
                <h3>"{highlightQuote}"</h3>
              </div>
            )}

            {ctaText && (
              <div>
                <a href={ctaLink} className="pm-abt-action-btn">
                  <span>{ctaText}</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* 3. Optional Team Section */}
      {showTeamSection && teamMembers && teamMembers.length > 0 && (
        <section className="pm-abt-team-section">
          <div className="container">
            <div className="pm-abt-team-header">
              <span className="pm-abt-tag-pill">{teamTagline}</span>
              <h2 className="pm-abt-block-title" style={{ marginTop: '8px' }}>{teamTitle}</h2>
            </div>
            <div className="pm-abt-team-grid">
              {teamMembers.map((member, idx) => (
                <div
                  key={idx}
                  className="pm-abt-team-card wow fadeInUp"
                  data-wow-delay={`${(idx + 1) * 100}ms`}
                >
                  <div className="pm-abt-team-img-wrap">
                    <img
                      src={getMediaUrl(member.image) || '/assets/images/team/team-1-1.jpg'}
                      alt={member.name}
                      className="pm-abt-team-img"
                      onError={(e) => {
                        e.target.src = '/assets/images/team/team-1-1.jpg';
                      }}
                    />
                  </div>
                  <div className="pm-abt-team-info">
                    <h3 className="pm-abt-team-name">{member.name}</h3>
                    <p className="pm-abt-team-role">{member.role}</p>
                    {member.bio && <p className="pm-abt-team-bio">{member.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default AboutPage;
